import {Component, computed, inject} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {FormArray} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {TimesheetStatusSummary} from '../../../../../../../api';
import {TimesheetStatusFormModel} from '../../../../../../features/timesheets/models/timesheet-status-form-model';
import {TimesheetStatusQueryService} from '../../../../../../features/timesheets/services/timesheet-status-query.service';
import {
  TimesheetStatusCommandService
} from '../../../../../../features/timesheets/services/timesheet-status-command.service';
import {TextInput} from '../../../../../../components/text-input/text-input';
import {ElciaButton} from '../../../../../../components/buttons/button/button';
import {ColorPicker} from '../../../../../../components/color-picker/color-picker';

@Component({
  selector: 'elcia-timesheet-settings-status',
  imports: [TextInput, ElciaButton, TranslatePipe, ColorPicker],
  templateUrl: './timesheet-settings-status.html',
  styleUrl: './timesheet-settings-status.scss',
})
export class TimesheetSettingsStatus {

  private readonly queryService = inject(TimesheetStatusQueryService);
  private readonly commandService = inject(TimesheetStatusCommandService);

  isLoading = computed(() => this.queryService.isLoading());

  statusForms = computed(() => {
    const formArray = new FormArray<FormGroup<TimesheetStatusFormModel>>([]);
    for (const status of this.queryService.statuses()) {
      const fg = this.createFormGroup(status);
      fg.disable();
      formArray.push(fg);
    }
    return formArray;
  });

  createForm = new FormGroup<Omit<TimesheetStatusFormModel, 'id'>>({
    nameEn: new FormControl('', Validators.required),
    nameFr: new FormControl('', Validators.required),
    nameNl: new FormControl('', Validators.required),
    color: new FormControl('#808080'),
  });

  private createFormGroup(status: TimesheetStatusSummary): FormGroup<TimesheetStatusFormModel> {
    return new FormGroup<TimesheetStatusFormModel>({
      id: new FormControl(status.id),
      nameEn: new FormControl(status.nameEn ?? '', Validators.required),
      nameFr: new FormControl(status.nameFr ?? '', Validators.required),
      nameNl: new FormControl(status.nameNl ?? '', Validators.required),
      color: new FormControl(status.color ?? '#808080'),
    });
  }

  private getFormGroupById(id: number | null | undefined) {
    return this.statusForms().controls.find(c => c.value.id === id);
  }

  toggleFormGroup(id: number | null | undefined) {
    const fg = this.getFormGroupById(id);
    if (!fg) return;
    if (fg.enabled) {
      if (fg.invalid) return;
      this.commandService.updateMutation.mutate({
        id: fg.value.id!,
        body: {
          nameFr: fg.value.nameFr,
          nameEn: fg.value.nameEn,
          nameNl: fg.value.nameNl,
          color: fg.value.color,
        },
      });
      fg.disable();
    } else {
      fg.enable();
    }
  }

  deleteStatus(id: number | null | undefined) {
    if (id == null) return;
    this.commandService.deleteMutation.mutate(id);
  }

  submitCreate() {
    if (this.createForm.invalid) return;
    this.commandService.createMutation.mutate(
      {
        nameFr: this.createForm.value.nameFr,
        nameEn: this.createForm.value.nameEn,
        nameNl: this.createForm.value.nameNl,
        color: this.createForm.value.color,
      },
      {onSuccess: () => this.createForm.reset({color: '#808080'})}
    );
  }
}
